import { S25Util } from "../../../../../../util/s25-util";
import { RecordType, S25Profile } from "../../../../ProfileI";
import { S25ProfileUtil } from "../../../../utils/s25.profile.util";
import { EventOccUtil } from "../../s25.event.occurrence.util";

export const COPY_NEW_SEGMENT_KEY = "new";

export const MIN_NEW_SEGMENT_NAME_LENGTH = 2;

export function segmentOptionId(profile: S25Profile): number {
    const profileId = profile.itemId ?? profile.profileId;
    return S25Util.parseInt(profileId);
}

export function segmentOptionKey(profile: S25Profile): string {
    return String(segmentOptionId(profile));
}

export function segmentOptionLabel(profile: S25Profile): string {
    return profile.itemName ?? profile.name ?? `Segment ${segmentOptionId(profile)}`;
}

export function copySegmentProfileOptions(profiles: S25Profile[] | undefined): S25Profile[] {
    return (profiles ?? []).filter((profile) => Number.isFinite(segmentOptionId(profile)));
}

export function isCopyTargetSourceSegment(
    copyTargetProfileKey: string,
    copySourceProfileId: number | undefined,
): boolean {
    if (copyTargetProfileKey === COPY_NEW_SEGMENT_KEY) {
        return false;
    }
    const targetProfileId = S25Util.parseInt(copyTargetProfileKey);
    return targetProfileId === copySourceProfileId;
}

export function resolvedCopyTargetProfileId(copyTargetProfileKey: string): number | undefined {
    if (copyTargetProfileKey === COPY_NEW_SEGMENT_KEY) {
        return undefined;
    }
    return S25Util.parseInt(copyTargetProfileKey);
}

export type CopySegmentPatternWarningParams = {
    copyTargetProfileKey: string;
    copySourceProfileId: number | undefined;
    profiles: S25Profile[] | undefined;
    repeatPatternOnSource: boolean;
};

export function copySegmentPatternChangeWarning(params: CopySegmentPatternWarningParams): string | undefined {
    const { copyTargetProfileKey, copySourceProfileId, profiles, repeatPatternOnSource } = params;
    if (copyTargetProfileKey === COPY_NEW_SEGMENT_KEY) {
        return undefined;
    }
    const targetProfileId = resolvedCopyTargetProfileId(copyTargetProfileKey);
    if (!targetProfileId || !copySourceProfileId) {
        return undefined;
    }
    if (repeatPatternOnSource) {
        return undefined;
    }

    const targetProfile = S25ProfileUtil.getProfileByProfileId(profiles ?? [], targetProfileId);
    const currentRepeatDescription = copyTargetRepeatDescription(targetProfile);
    if (!currentRepeatDescription) {
        return undefined;
    }

    return `Adding this occurrence will remove the repeating pattern from this segment. ${currentRepeatDescription}`;
}

function copyTargetRepeatDescription(profile: S25Profile | undefined): string | undefined {
    const profileCode = profile?.occurrenceDefn?.profileCode?.trim();
    if (!profile || !segmentHasRepeatingPattern(profile) || !profileCode) {
        return undefined;
    }
    const repetitionText = EventOccUtil.getRepetitionText(profileCode);
    if (!repetitionText) {
        return undefined;
    }
    if (repetitionText.startsWith("Repeats ")) {
        return `It currently repeats ${repetitionText.slice("Repeats ".length)}.`;
    }
    return `It currently ${repetitionText.charAt(0).toLowerCase()}${repetitionText.slice(1)}.`;
}

function segmentHasRepeatingPattern(profile: S25Profile): boolean {
    const defn = profile.occurrenceDefn;
    if (!defn || defn.recTypeId !== RecordType.RecurrenceGrammar) {
        return false;
    }
    const code = defn.profileCode?.trim() ?? "";
    return !!code && !["dnr", "adhoc"].includes(code);
}

export function newSegmentNameValidationError(segmentName: string | undefined): string | undefined {
    const trimmed = segmentName?.trim() ?? "";
    if (trimmed.length >= MIN_NEW_SEGMENT_NAME_LENGTH) {
        return undefined;
    }
    if (!trimmed) {
        return "New segment name is required.";
    }
    return "Please enter a new segment name of at least 2 characters.";
}

export function isNewSegmentNameValid(segmentName: string | undefined): boolean {
    return newSegmentNameValidationError(segmentName) === undefined;
}
